/**
 * Recent global-search terms, persisted per-browser in localStorage.
 *
 * Two kinds of search land here, and they replay differently:
 *   - 'single' — one term typed into the top bar; replaying re-runs the inline
 *     search so the dropdown can be picked through again.
 *   - 'bulk'   — a pasted list ("5018029,5018030,5018031"), stored comma-joined.
 *     A bulk paste goes straight to the batch results page, so replaying one
 *     navigates there rather than feeding the single-term search.
 *
 * The top bar records every term that came back with results, so a single typing
 * run ("GW" -> "GWI" -> "GWI123") would otherwise eat three of the ten slots.
 * `addRecentSearch` collapses those against the newest entry: if the incoming
 * term and the newest one are prefix-related, the incoming term replaces it
 * rather than stacking on top. Collapsing looks at the newest entry only, so an
 * older "GWI12" search stays put when you later type "GWI123", and it never
 * crosses kinds — a bulk "GWI1,GWI2" must not swallow a single "GWI1" that it
 * happens to begin with.
 */

export const RECENT_SEARCHES_KEY = 'globalSearch_recentSearches';
export const MAX_RECENT_SEARCHES = 10;

export type RecentSearchKind = 'single' | 'bulk';

export interface RecentSearch {
  /** The search text: one term for 'single', comma-joined terms for 'bulk'. */
  term: string;
  kind: RecentSearchKind;
}

/** Below this the term is noise (single keystrokes), and matches the search's own 2-char floor. */
const MIN_TERM_LENGTH = 2;

const sameTerm = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** True when either term is a prefix of the other — i.e. both sides of one typing run. */
const prefixRelated = (a: string, b: string) => {
  const lowerA = a.toLowerCase();
  const lowerB = b.toLowerCase();
  return lowerA.startsWith(lowerB) || lowerB.startsWith(lowerA);
};

/** Split a stored bulk term back into its individual search terms. */
export function bulkTerms(entry: RecentSearch): string[] {
  return entry.term.split(',').map((t) => t.trim()).filter(Boolean);
}

/**
 * Coerce one stored element into an entry, or null if it is unusable. Accepts
 * the legacy plain-string format (pre-bulk) as a single-term search.
 */
function normalize(stored: unknown): RecentSearch | null {
  if (typeof stored === 'string') {
    const term = stored.trim();
    return term ? { term, kind: 'single' } : null;
  }
  if (!stored || typeof stored !== 'object') return null;
  const { term, kind } = stored as { term?: unknown; kind?: unknown };
  if (typeof term !== 'string') return null;
  const trimmed = term.trim();
  if (!trimmed) return null;
  return { term: trimmed, kind: kind === 'bulk' ? 'bulk' : 'single' };
}

/**
 * Read the stored searches, newest first. Never throws: a corrupt payload, a
 * non-array payload, or a localStorage that refuses to be read (private mode,
 * blocked storage) all come back as an empty list.
 */
export function loadRecentSearches(): RecentSearch[] {
  try {
    const rawValue = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!rawValue) return [];
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalize)
      .filter((entry): entry is RecentSearch => entry !== null)
      .slice(0, MAX_RECENT_SEARCHES);
  } catch {
    return [];
  }
}

/**
 * Record a search and return the updated list (newest first). Terms shorter than
 * two characters are ignored. Repeating a search moves it to the front in its new
 * casing instead of duplicating it.
 */
export function addRecentSearch(term: string, kind: RecentSearchKind = 'single'): RecentSearch[] {
  const trimmed = term.trim();
  const existing = loadRecentSearches();
  if (trimmed.length < MIN_TERM_LENGTH) return existing;

  const head = existing[0];
  // Same typing run as the newest entry? Take its slot instead of adding one.
  // Only single-term searches type their way up like this.
  const collapsesHead =
    head !== undefined &&
    kind === 'single' &&
    head.kind === 'single' &&
    prefixRelated(head.term, trimmed);

  const rest = collapsesHead ? existing.slice(1) : existing;
  const next = [
    { term: trimmed, kind },
    ...rest.filter((entry) => !sameTerm(entry.term, trimmed)),
  ].slice(0, MAX_RECENT_SEARCHES);

  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
  } catch {
    // Storage full or blocked — the caller still gets the updated list for this session.
  }
  return next;
}

/** Forget every recorded search. */
export function clearRecentSearches(): void {
  try {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch {
    // Nothing to do — the list is already unreachable.
  }
}
